import { QuestionType2UIConfig } from "../../../../../components/ui/question-type2-ui";
import { GeneralQuestionUIConfig, GeneralQuestionUIConfigOption } from "../../../../../cvp-webgames-core/Components/ui/react/core-general-question-ui";
import BaseScenarioPart from "../../../../../cvp-webgames-core/Scenario/BaseScenarioPart";
import { VideoSegmentItem, SingleVideoSegmentItem } from "../../../../../cvp-webgames-core/VideoSegment/VideoSegmentItem";
import { ScoringType } from "../../../../../misc/tools";
import { GameBaseScenario } from "../../../../GameBaseScenario";
import Scenario9GtubeMedAdm, { scenario9GtubeMedAdmAudioCaching, scenario9GtubeMedAdmVideoSegments } from "../../../scenario9-gtube-med-adm";
import { GtubeMedAdmComponentsStep1ComponentPart1Config, GtubeMedAdmComponentsStep1ComponentMainConfig } from "../gtube-med-adm-components-step1";

export default class GtubeMedAdmComponentsStep1Part1 extends BaseScenarioPart<GameBaseScenario> {
	private stepName = 'Step 2'
	private get config(): GtubeMedAdmComponentsStep1ComponentPart1Config {
		return this.mainConfig.part1Config;
	}
	constructor(scenario: GameBaseScenario, private onDone: () => void, private id: string, private mainConfig: GtubeMedAdmComponentsStep1ComponentMainConfig) {
		super(scenario);
	}

	public get partId(): string {
		return this.id;
	}

	public get videoUrl(): string[] {
		return [this.config.videoUrl];
	}

	public start() {
		this.section1();
	}

	public section1() {
		const context = this;
        this.mainConfig.scenario.setAreaForCurrentScenario(6);
		this.mainConfig.scenario.uiWrapper.displayMixedMediaUI({
			title: this.mainConfig.scenario.props.name,
			subtitle: this.stepName,
			htmlData: `
			<p class="default-text add-pad">For the substitution test, you approach other employees and inquire as to whether they would do the same 
			thing in similar circumstances.<br/><br/>
			Nurse #1: I have not experienced a similar event. I could see how someone 
			would confuse the two bottles. They are the same size and color. Also, BCMA system does not require 
			scanning the sterile water bottle when mixing medications for g-tube administration.<br/><br/>
			Nurse #2: This has never happened to me. I know the difference between the 
			two bottles, but maybe a brand-new nurse wouldn’t be able to know how to detect this.<br/><br/>
			Nurse #3: The bottles are very similar. A nurse should know the difference, 
			but I can see how the two could be mixed up. There are a lot of solutions at the bedside.</p>
			`,
			okButton: 'Continue',
			talkingVideoSrc: scenario9GtubeMedAdmVideoSegments.getItemByKey('MixedMedia').src,
			talkingVideoChapterId: scenario9GtubeMedAdmVideoSegments.getItemByKey('MixedMedia').chapterIds[0],
			onContinueClicked: () => {
				context.section2();
			}
		})
	}

	public section2() {
		const context = this;
		this.mainConfig.scenario.uiWrapper.displayQuestonType2UI({
			title: this.mainConfig.scenario.props.name,
			audioUrl: scenario9GtubeMedAdmAudioCaching.getItemByKey(scenario9GtubeMedAdmAudioCaching.audioKeys.question3).src,
			subtitle: (this.stepName + ' - Question 1'),
			isYesCorrect: true,
			correctFeedback: `
				<p class="default-text">Correct. The other nurses indicated how it would be easy to mix up 
				the solutions, and that there is an issue with multiple bottles/solutions at the bedside. 
				Even though all said they had not experienced the same situation, or admitted to being involved 
				in a similar event, their descriptions tell you that given the system, it could have been them.</p>
			`,
			wrongFeedback: `
				<p class="default-text">This is not the best option. The other nurses generally supported how 
				it would be easy to mix up the solutions, and that there is an issue with multiple bottles/solutions 
				at the bedside. Even though all said they had not experienced the same situation, or admitted to 
				being involved in a similar event, their descriptions tell you that given the system, it could have been them.</p>
			`,
			question: 'Given the information received from staff interviews, is it reasonable to conclude that a systems or cultural issue exists, and other employees in that work area would behave the same way in a similar situation?',
			onSubmitClicked: (isCorrect: boolean, feedback: string, selectedTitle: string, parentQuestion: QuestionType2UIConfig) => {
				const mentorInfo = scenario9GtubeMedAdmVideoSegments.getItemByKey('Feedback');
				context.scenario.onChoiceSubmittedQuestionType2(isCorrect, feedback, selectedTitle, (this.stepName + ' - Question 1 '), parentQuestion, () => {
					context.section3();
				}, () => {
					context.section3();
				}, undefined, undefined, undefined, mentorInfo.src, mentorInfo.chapterIds[6], mentorInfo.chapterIds[7]);
			}
		}, 2);
	}

	public section3() {
		const context = this;
		this.mainConfig.scenario.uiWrapper.displayQuestonType1UI({
			title: this.mainConfig.scenario.props.name,
			audioUrl: scenario9GtubeMedAdmAudioCaching.getItemByKey(scenario9GtubeMedAdmAudioCaching.audioKeys.question4).src,
			subtitle: (this.stepName + ' - Question 2'),
			questionTitle: 'Based on the substitution test, the accountability for this action…',
			options: [
				{
					title: 'Lies completely with the employee.',
					description: 'Select and submit an option.',
					feedbackHtml: `
						<p class="default-text">This is not the best option. There is no history involving this type 
						of behavior or decision making with this employee. The repetitive occurrences section of the 
						decision support tool should be reviewed and considered prior to making your decision.</p>
					`,
					isCorrect: false,
					score: ScoringType.bad
				},
				{
					title: 'Is shared between the employee and organizational leadership.',
					description: 'Select and submit an option.',
					feedbackHtml: `
						<p class="default-text">This is not the best choice. The substitution test demonstrates that 
						no other employees would make a similar choice and there are no system or cultural issues identified.</p>
					`,
					isCorrect: false,
					score: ScoringType.bad
				},
				{
					title: 'Lies solely with the leadership.',
					description: 'Select and submit an option.',
					feedbackHtml: `
						<p class="default-text">Correct. The substitution test demonstrates that there are system or cultural issues identified.</p>
					`,
					isCorrect: true,
					score: ScoringType.good
				}
			],
			
			onSubmitClicked: (allOptions: GeneralQuestionUIConfigOption[], selectedChoices: GeneralQuestionUIConfigOption[], parentQuestionConfig: GeneralQuestionUIConfig) => {
				const mentorInfo = scenario9GtubeMedAdmVideoSegments.getItemByKey('Feedback');
				context.scenario.onChoiceSubmittedQuestionType1(selectedChoices[0], (this.stepName + ' - Question 2 '), parentQuestionConfig, () => {
					this.onDone();
				}, () => {
					this.onDone();
				}, undefined, undefined, undefined, mentorInfo.src, mentorInfo.chapterIds[8], mentorInfo.chapterIds[9], mentorInfo.chapterIds[10], 'Lies completely with the employee.');
			}
		}, 3);
	}
}